import { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";
import { SITE } from "../../lib/data";

const NAV = [
  { id: "projects", label: "Projects", testIdSlug: "projects" },
  { id: "gallery", label: "Estate", testIdSlug: "estate" },
  { id: "highlights", label: "Highlights", testIdSlug: "highlights" },
  { id: "plots", label: "Plots", testIdSlug: "plots" },
  { id: "villas", label: "Villas", testIdSlug: "villas" },
  { id: "amenities", label: "Amenities", testIdSlug: "amenities" },
  { id: "map", label: "Location", testIdSlug: "location" },
];

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.641 0 5.124 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.49-8.413z"/>
  </svg>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#F7F5F0]/92 backdrop-blur-xl border-b border-[#1A362D]/10"
          : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 lg:px-10 py-4">
        <button
          data-testid="logo-button"
          onClick={() => go("top")}
          className="flex items-center"
          aria-label="Amarja Hills home"
        >
          <img
            src={SITE.logo}
            alt="Amarja Hills"
            className={`h-12 md:h-14 w-auto transition-all duration-500 ${
              scrolled ? "logo-on-cream" : "logo-on-dark"
            }`}
          />
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-${n.testIdSlug}`}
              onClick={() => go(n.id)}
              className={`text-[11px] tracking-[0.24em] uppercase font-medium transition-colors ${
                scrolled
                  ? "text-[#1A362D] hover:text-[#987F53]"
                  : "text-[#F7F5F0]/90 hover:text-[#E2CFA1]"
              }`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            data-testid="header-instagram"
            href={SITE.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className={`transition-colors ${
              scrolled ? "text-[#1A362D] hover:text-[#987F53]" : "text-[#F7F5F0] hover:text-[#E2CFA1]"
            }`}
          >
            <Instagram className="w-[18px] h-[18px]" strokeWidth={1.6} />
          </a>
          <a
            data-testid="header-whatsapp"
            href={`https://wa.me/${SITE.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="WhatsApp"
            className={`transition-colors ${
              scrolled ? "text-[#1A362D] hover:text-[#987F53]" : "text-[#F7F5F0] hover:text-[#E2CFA1]"
            }`}
          >
            <WhatsAppIcon className="w-[18px] h-[18px]" />
          </a>
          <a
            data-testid="header-brochure-link"
            href="/brochure/Amarja-Global-Investor-Brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className={`text-[11px] tracking-[0.22em] uppercase font-medium border-b pb-0.5 transition-colors ${
              scrolled
                ? "text-[#987F53] hover:text-[#1A362D] border-[#987F53]/40 hover:border-[#1A362D]"
                : "text-[#E2CFA1] hover:text-[#F7F5F0] border-[#E2CFA1]/50 hover:border-[#F7F5F0]"
            }`}
          >
            Brochure ↓
          </a>
          <a
            data-testid="header-rent-link"
            href={SITE.experienceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-[11px] tracking-[0.22em] uppercase font-medium border-b pb-0.5 transition-colors ${
              scrolled
                ? "text-[#987F53] hover:text-[#1A362D] border-[#987F53]/40 hover:border-[#1A362D]"
                : "text-[#E2CFA1] hover:text-[#F7F5F0] border-[#E2CFA1]/50 hover:border-[#F7F5F0]"
            }`}
          >
            Rent a Villa →
          </a>
          <span
            aria-hidden="true"
            className={`h-4 w-px ${scrolled ? "bg-[#1A362D]/20" : "bg-[#F7F5F0]/25"}`}
          />
          <button
            data-testid="header-enquire-button"
            onClick={() => go("enquiry")}
            className={scrolled ? "btn-primary" : "btn-ghost"}
          >
            Enquire to Buy
          </button>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className={scrolled ? "lg:hidden text-[#1A362D]" : "lg:hidden text-[#F7F5F0]"}
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F7F5F0] border-t border-[#1A362D]/10 px-6 py-6 space-y-4"
        >
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`mobile-nav-${n.testIdSlug}`}
              onClick={() => go(n.id)}
              className="block w-full text-left font-serif-display text-2xl text-[#1A362D] font-light"
            >
              {n.label}
            </button>
          ))}
          <div className="pt-4 border-t border-[#1A362D]/10 flex items-center gap-6">
            <a
              data-testid="mobile-instagram"
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1A362D]"
              aria-label="Instagram"
            >
              <Instagram className="w-5 h-5" strokeWidth={1.6} />
            </a>
            <a
              data-testid="mobile-whatsapp"
              href={`https://wa.me/${SITE.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1A362D]"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon className="w-5 h-5" />
            </a>
            <a
              data-testid="mobile-phone"
              href={`tel:${SITE.telHref}`}
              className="text-sm tracking-[0.2em] uppercase text-[#1A362D] font-semibold"
            >
              Call
            </a>
            <button
              data-testid="mobile-enquire-button"
              onClick={() => go("enquiry")}
              className="btn-primary ml-auto"
            >
              Enquire
            </button>
          </div>
          <a
            data-testid="mobile-rent-link"
            href={SITE.experienceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block pt-3 text-sm tracking-[0.18em] uppercase text-[#987F53] font-semibold border-t border-[#1A362D]/10"
          >
            Looking to rent? Visit amarjaexperience.com →
          </a>
        </div>
      )}
    </header>
  );
}
